import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, User, LogOut } from "lucide-react";
import { getUser, clearAuth, isStaff } from "@/lib/auth";

const NAV_LINKS = [
  { label: "Viaturas", href: "/viaturas" },
  { label: "Importar", href: "/importar" },
  { label: "Financiamento", href: "/simulador" },
  { label: "Contactos", href: "/contactos" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-md" : "shadow-none border-b border-slate-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#inicio" data-testid="header-logo" className="flex items-center select-none">
            <img src="/assets/logo-dark.png" alt="Solution Car Premium" className="h-14 w-auto" />
          </a>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-slate-600 hover:text-[#0072C6] transition-colors tracking-wide"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/#contacto"
              data-testid="header-cta-button"
              className="ml-1 inline-flex items-center gap-2 bg-[#0072C6] text-white text-sm font-semibold px-6 py-3 hover:bg-[#005FA3] transition-colors"
            >
              Pedir Cotação
            </a>
            {(() => {
              const u = getUser();
              return u ? (
                <Link
                  to={isStaff() ? "/admin/painel" : "/conta"}
                  data-testid="header-account"
                  className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm font-medium px-5 py-3 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors"
                >
                  <User className="w-4 h-4" /> {isStaff() ? "Painel" : "Conta"}
                </Link>
              ) : (
                <Link
                  to="/entrar"
                  data-testid="header-login"
                  className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm font-medium px-5 py-3 hover:border-[#0072C6] hover:text-[#0072C6] transition-colors"
                >
                  <User className="w-4 h-4" /> Entrar
                </Link>
              );
            })()}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#0072C6]"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-medium text-slate-700 border-b border-slate-100 last:border-0"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contacto"
              data-testid="mobile-header-cta-button"
              onClick={() => setOpen(false)}
              className="mt-4 mb-2 inline-flex items-center justify-center gap-2 bg-[#0072C6] text-white font-semibold px-6 py-3.5"
            >
              Pedir Cotação
            </a>
            <Link
              to="/entrar"
              data-testid="mobile-header-login"
              onClick={() => setOpen(false)}
              className="mb-2 inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold px-6 py-3.5"
            >
              <User className="w-4 h-4" /> Área de Cliente
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
